import React from 'react';

import {ApiUserProfile} from 'src/lib/api/auth';
import {TimeZoneUTC} from 'src/lib/dates';
import {isUpstreamInternalOrgUser} from 'src/lib/userUtils';

import {StatusItem} from './StatusBar';

export type TimezoneMode = 'siteLocal' | 'utc';

type TimezoneContextType = {
  timezoneMode: TimezoneMode;
  setTimezoneMode: (mode: TimezoneMode) => void;
  getDisplayTimeZone: (localTimeZone: string) => string;
  isTimezoneToggleEnabled: boolean;
};

const TimezoneContext = React.createContext<TimezoneContextType | undefined>(undefined);

export function TimezoneProvider({
  children,
  currentUser,
}: {
  children: React.ReactNode;
  currentUser: ApiUserProfile | null;
}) {
  const [timezoneMode, setTimezoneMode] = React.useState<TimezoneMode>('siteLocal');

  // Check if the current user has access to timezone toggle functionality
  const isTimezoneToggleEnabled = React.useMemo(() => {
    return isUpstreamInternalOrgUser(currentUser);
  }, [currentUser]);

  const getDisplayTimeZone = (localTimeZone: string): string => {
    return timezoneMode === 'utc' ? TimeZoneUTC : localTimeZone;
  };

  // Create a setTimezoneMode function that only works if timezone toggle is enabled
  const conditionalSetTimezoneMode = React.useCallback(
    (newMode: TimezoneMode) => {
      if (isTimezoneToggleEnabled) {
        setTimezoneMode(newMode);
      }
    },
    [isTimezoneToggleEnabled]
  );

  const value = {
    timezoneMode,
    setTimezoneMode: conditionalSetTimezoneMode,
    getDisplayTimeZone,
    isTimezoneToggleEnabled,
  };

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

// TODO: Should these hooks be in hooks/ ?

// Custom hook to use timezone
export function useTimezone() {
  const context = React.useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}

/**
 * Hook to enable keyboard shortcut to toggle timezone
 */
export function useTimezoneWithShortcut() {
  const {timezoneMode, setTimezoneMode, getDisplayTimeZone, isTimezoneToggleEnabled} =
    useTimezone();

  React.useEffect(() => {
    // Only enable keyboard shortcut if timezone toggle is enabled
    if (!isTimezoneToggleEnabled) {
      return;
    }

    let zCount = 0;
    let timer: NodeJS.Timeout;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && (event.key === 'z' || event.key === 'Z')) {
        event.preventDefault();
        zCount++;

        if (zCount === 2) {
          setTimezoneMode(timezoneMode === 'utc' ? 'siteLocal' : 'utc');
          zCount = 0;
        }

        clearTimeout(timer);
        timer = setTimeout(() => {
          zCount = 0;
        }, 500);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timer);
    };
  }, [timezoneMode, setTimezoneMode, isTimezoneToggleEnabled]);

  return {timezoneMode, setTimezoneMode, getDisplayTimeZone, isTimezoneToggleEnabled};
}
